const m = require('minthril');
const { component } = require('../../component');
const {
  loadMcpTokenStatus,
  revokeMcpToken
} = require('../../controllers/billing');
const { loadMcpConnections, mcpServerUrl, revokeMcpConnection } = require('../../controllers/mcp');

const accountSections = [
  { href: '/account', label: 'Overview', key: 'overview' },
  { href: '/account/billing', label: 'Billing', key: 'billing' },
  { href: '/account/developer', label: 'Developer access', key: 'developer' }
];

const AccountNavigation = component(() => ({
  view: ({ attrs: { active } }) => m('nav.account-navigation', {
    'aria-label': 'Account sections'
  }, accountSections.map(section => m('a.account-navigation__link', {
    href: section.href,
    'aria-current': active === section.key ? 'page' : null
  }, section.label)))
}));

const AccountPage = component(() => ({
  view: ({ attrs: { user } }) => m('section.panel.account-page', [
    m('h2', 'Account'),
    m(AccountNavigation, { active: 'overview' }),
    m('section.account-page__identity', [
      m('h3', 'Your account'),
      m('p.muted', user.email || 'Signed in to Spache')
    ]),
    m('div.account-page__cards', [
      m('a.account-page__card', { href: '/account/billing' }, [
        m('h3', 'Billing'),
        m('p.muted', 'View usage, plan status, and payment settings.')
      ]),
      m('a.account-page__card', { href: '/account/developer' }, [
        m('h3', 'Developer access'),
        m('p.muted', 'Connect a coding agent to Spache with OAuth.')
      ])
    ])
  ])
}));

const DeveloperAccessPage = component((vnode, emit) => {
  let checkedUserId = null;
  let loading = false;
  let changing = false;
  let status = null;
  let connections = [];
  let error = null;

  async function load (user) {
    checkedUserId = user.uid;
    loading = true;
    error = null;
    try {
      const [tokenStatus, connectionResult] = await Promise.all([
        loadMcpTokenStatus(user),
        loadMcpConnections(user)
      ]);
      status = tokenStatus;
      connections = connectionResult.connections;
    } catch (reason) {
      error = reason.message;
    } finally {
      loading = false;
      emit('redraw');
    }
  }

  async function revokeConnection (user, connectionId) {
    changing = true;
    error = null;
    try {
      await revokeMcpConnection(user, connectionId);
      connections = connections.map(connection => connection.id === connectionId
        ? { ...connection, revoked: true }
        : connection);
    } catch (reason) {
      error = reason.message;
    } finally {
      changing = false;
      emit('redraw');
    }
  }

  async function revokeLegacyToken (user) {
    changing = true;
    error = null;
    try {
      await revokeMcpToken(user);
      status = { configured: false, createdAt: null };
    } catch (reason) {
      error = reason.message;
    } finally {
      changing = false;
      emit('redraw');
    }
  }

  return {
    view: ({ attrs: { user } }) => {
      if (checkedUserId !== user.uid && !loading) load(user);

      return m('section.panel.account-page', [
        m('h2', 'Developer access'),
        m(AccountNavigation, { active: 'developer' }),
        m('section.account-page__developer', [
          m('h3', 'Connect a coding agent to Spache'),
          m('p.muted', 'Add Spache as a remote MCP server in your coding agent. When it connects, Spache will open a browser sign-in and ask you to approve the requested access.'),
          m('p', m('code', mcpServerUrl)),
          error && m('p.error', error),
          loading && m('p.muted', 'Checking developer access…'),
          !loading && status && [
            m('section.account-page__connections', [
              m('h3', 'Connected agents'),
              connections.filter(connection => !connection.revoked).length
                ? connections.filter(connection => !connection.revoked).map(connection =>
                  m('div.account-page__connection', [
                    m('div', [
                      m('strong', connection.clientName || 'MCP client'),
                      m('p.muted', `Connected ${new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(connection.createdAt))}`),
                      m('p.muted', connection.scopes.join(', '))
                    ]),
                    m('button.button.button--quiet.button--danger', {
                      type: 'button',
                      disabled: changing,
                      onclick: () => revokeConnection(user, connection.id)
                    }, 'Disconnect')
                  ]))
                : m('p.muted', 'No coding agents are connected yet.'),
              status.configured && m('div.account-page__legacy-token', [
                m('p.muted', 'A legacy local MCP token is still active. It is separate from OAuth connections.'),
                m('button.button.button--quiet.button--danger', {
                  type: 'button',
                  disabled: changing,
                  onclick: () => revokeLegacyToken(user)
                }, 'Revoke legacy token')
              ])
            ])
          ]
        ])
      ]);
    }
  };
});

module.exports = { AccountNavigation, AccountPage, DeveloperAccessPage };
